<script module lang="ts">
  // Editor-side LinkCard — the draggable row in the design editor's
  // link list. Distinct from ProfileLinkCard (the public-facing card).
  // Every badge permutation (active / paused / scheduled / expiring /
  // has thumbnail / high clicks / embed_type) gets a pinned story so a
  // small CSS change in the badges row can't silently break a state.
  import { defineMeta } from '@storybook/addon-svelte-csf'
  import LinkCard from './LinkCard.svelte'
  import type { EditorLink } from '../types'

  function link(overrides: Partial<EditorLink> = {}): EditorLink {
    return {
      id: 'story-link',
      title: 'AI 기본법 시행령 핵심 정리',
      url: 'https://axhub.space/posts/ai-basic-act',
      description: '2026년 시행 예정 주요 조항 분석',
      emoji: '📄',
      link_type: 'post',
      position: 0,
      is_active: true,
      clicks_count: 128,
      visibility: 'public',
      scheduled_at: null,
      expires_at: null,
      embed_type: null,
      link_group_id: null,
      thumbnail_url: null,
      ...overrides,
    }
  }

  const { Story } = defineMeta({
    component: LinkCard,
    title: 'Components/ProfileEditor/LinkCard',
    tags: ['autodocs'],
    parameters: { layout: 'centered' },
  })
</script>

<Story name="Active · default state">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard link={link()} />
    </div>
  {/snippet}
</Story>

<Story name="Paused · is_active false">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard link={link({ is_active: false })} />
    </div>
  {/snippet}
</Story>

<Story name="Dragging · visual lift">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard link={link()} dragging={true} />
    </div>
  {/snippet}
</Story>

<Story name="With thumbnail">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard
        link={link({
          thumbnail_url: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=240&q=80&auto=format&fit=crop',
          title: 'Cursor vs Copilot 3개월 비교',
          description: 'DX실 실사용 후기',
          clicks_count: 256,
        })}
      />
    </div>
  {/snippet}
</Story>

<Story name="Scheduled · future start date">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard
        link={link({
          scheduled_at: '2026-04-15T09:00:00.000Z',
          description: '공개 예약된 링크',
        })}
      />
    </div>
  {/snippet}
</Story>

<Story name="Expiring · future end date">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard
        link={link({
          expires_at: '2026-04-30T23:59:00.000Z',
          description: '2026년 4월까지만 노출',
        })}
      />
    </div>
  {/snippet}
</Story>

<Story name="Both schedules · start and expire">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard
        link={link({
          scheduled_at: '2026-04-15T09:00:00.000Z',
          expires_at: '2026-04-30T23:59:00.000Z',
          title: '한정 기간 이벤트 링크',
          description: '2주간 공개',
        })}
      />
    </div>
  {/snippet}
</Story>

<Story name="Embed type · youtube">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard
        link={link({
          embed_type: 'youtube',
          title: '2025 AI 컨퍼런스 키노트',
          emoji: '▶️',
          url: 'https://www.youtube.com/watch?v=...',
        })}
      />
    </div>
  {/snippet}
</Story>

<Story name="Embed type · calendar (new)">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard
        link={link({
          embed_type: 'calendar',
          title: '1:1 커피챗 예약',
          emoji: '📅',
          url: 'https://cal.com/seunghan/30min',
        })}
      />
    </div>
  {/snippet}
</Story>

<Story name="High clicks · large counter">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard link={link({ clicks_count: 12400 })} />
    </div>
  {/snippet}
</Story>

<Story name="No description · title only">
  {#snippet template()}
    <div style="width: 520px; padding: 24px;">
      <LinkCard link={link({ description: null, clicks_count: 0 })} />
    </div>
  {/snippet}
</Story>
